import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Box, Button, Card, CardActionArea, CardContent, Chip, Stack, Typography } from '@mui/material';
import type { FieldValues, TournamentPreset } from '@/api/generated/model';
import { FieldsForm, FormPage, initialFieldValues } from '@/components/manage';
import { LoadingState, ErrorState } from '@/components';
import { useSports } from '@/hooks/useApi';
import { useCreateFromPreset, useTournamentPresets } from '@/hooks/useManage';
import { useNotification } from '@/contexts/NotificationContext';
import { getApiErrorMessage, getApiFieldErrors } from '@/utils/apiError';
import { getSportOption, sortSports } from '@/utils/sports';
import type { SportType } from '@/types';

/** Создание турнира: шаг 1 — вид спорта и формат (пресет), шаг 2 — поля пресета с сервера. */
export const TournamentNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError } = useNotification();
  const { data: sports } = useSports();
  const [sportType, setSportType] = useState<SportType>((searchParams.get('sportType') as SportType) || 'football');
  const { data: presets, isLoading, error, refetch } = useTournamentPresets(sportType);
  const [preset, setPreset] = useState<TournamentPreset | null>(null);
  const [values, setValues] = useState<FieldValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const create = useCreateFromPreset();

  const fields = useMemo(() => (preset?.fields ?? []).filter((f) => f.key !== 'sportType'), [preset]);
  const activeSports = sortSports((sports ?? []).filter((s) => s.active));

  const choosePreset = (p: TournamentPreset) => {
    setPreset(p);
    setValues({ ...(p.defaults ?? {}), ...initialFieldValues(p.fields) });
    setErrors({});
    setDirty(false);
  };

  const submit = async () => {
    if (!preset) return;
    setErrors({});
    try {
      const tournament = await create.mutateAsync({ presetId: preset.id, values: { ...values, sportType } });
      setDirty(false);
      navigate(`/tournaments/${tournament.id}?tab=workflow`, { replace: true });
    } catch (err) {
      const fieldErrors = getApiFieldErrors(err);
      if (Object.keys(fieldErrors).length) setErrors(fieldErrors);
      else showError(getApiErrorMessage(err, 'Не удалось создать турнир.'));
    }
  };

  if (!preset) {
    return (
      <FormPage title="Новый турнир" onSubmit={() => undefined} submitDisabled submitLabel="Выберите формат" closeTo="/tournaments">
        <Typography variant="h5" sx={{ mb: 1 }}>
          Вид спорта
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
          {activeSports.map((s) => (
            <Chip
              key={s.code}
              label={`${getSportOption(s.code)?.icon ?? ''} ${s.name}`}
              color={s.code === sportType ? 'manage' : 'default'}
              variant={s.code === sportType ? 'filled' : 'outlined'}
              onClick={() => setSportType(s.code)}
              sx={{ height: 40, fontSize: '0.875rem' }}
            />
          ))}
        </Stack>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Формат
        </Typography>
        {isLoading && <LoadingState message="Загрузка форматов..." />}
        {error && <ErrorState message="Не удалось загрузить форматы" onRetry={refetch} />}
        <Stack spacing={1.5}>
          {presets?.map((p) => (
            <Card key={p.id} variant="outlined">
              <CardActionArea onClick={() => choosePreset(p)} sx={{ minHeight: 72 }}>
                <CardContent>
                  <Typography variant="h4">{p.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {p.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      </FormPage>
    );
  }

  return (
    <FormPage
      title={preset.title}
      onSubmit={() => void submit()}
      submitLabel="Создать"
      saving={create.isPending}
      dirty={dirty}
      closeTo="/tournaments"
      secondary={
        <Button variant="outlined" size="large" onClick={() => setPreset(null)} sx={{ minHeight: 48, flexShrink: 0 }}>
          Назад
        </Button>
      }
    >
      <Box sx={{ pt: 1 }}>
        <FieldsForm
          fields={fields}
          values={values}
          errors={errors}
          sportType={sportType}
          onChange={(next) => {
            setValues(next);
            setDirty(true);
          }}
        />
      </Box>
    </FormPage>
  );
};
